/**
 * Prompts a user when they exit the page
 * link: https://gist.github.com/MarksCode/64e438c82b0b2a1161e01c88ca0d0355
 */
import { useCallback, useContext, useEffect, useState } from 'react'
import { UNSAFE_NavigationContext as NavigationContext, useLocation } from 'react-router-dom'

import { IGNORE_EDITABLE_AREA_PROMPT_KEY } from 'src/core/constants/url'

type TConfirmExit = () => boolean

let timer = 0
let basePush: any

const throttlePush = ({
  ignorePrompt,
  args,
  confirmExit,
  isAddParameters,
}: {
  isAddParameters: boolean
  confirmExit: TConfirmExit
  args: any
  ignorePrompt: boolean
}) => {
  if (ignorePrompt) {
    basePush(...args)
    return
  }
  clearTimeout(timer)
  timer = window.setTimeout(() => {
    if (!isAddParameters) {
      const result = confirmExit()
      if (result !== false) {
        basePush(...args)
      }
    } else {
      basePush(...args)
    }
  }, 200)
}

function useConfirmExit(confirmExit: TConfirmExit, when = true) {
  const { navigator } = useContext(NavigationContext)
  const location = useLocation()

  useEffect(() => {
    if (!when) {
      return
    }

    // @ts-ignore
    if (!navigator.push.isModified) {
      basePush = navigator.push
      navigator.push = (...args: Parameters<typeof basePush>) => {
        const newPath = (args[0] as any)?.pathname || ''
        const oldPath = location.pathname
        const isAddParameters = newPath.includes(oldPath)

        const options = args[1]
        const ignorePrompt =
          !!options &&
          typeof options === 'object' &&
          IGNORE_EDITABLE_AREA_PROMPT_KEY in options &&
          options.ignorePrompt === true

        throttlePush({ isAddParameters, confirmExit, args, ignorePrompt })
      }
      // @ts-ignore
      navigator.push.isModified = true
    }

    return () => {
      navigator.push = basePush
    }
  }, [navigator, confirmExit, when])
}

export function usePrompt(message: string, when = true) {
  const [hasOperations, setHasOperations] = useState<boolean>(false)

  useEffect(() => {
    if (when) {
      setHasOperations(true)
      window.onbeforeunload = function () {
        return message
      }
    } else {
      setHasOperations(false)
    }

    return () => {
      window.onbeforeunload = null
    }
  }, [message, when])

  const confirmExit = useCallback(() => {
    const confirm = window.confirm(message)
    return confirm
  }, [message])
  useConfirmExit(confirmExit, when)

  return { hasOperations }
}
